import React, { useEffect, useRef, useState } from "react"
import styles from "./inboxCard.module.css"
import { MdMoreVert, MdOutlineWhatsapp } from "react-icons/md"
import ImageWithFallback from "../ImageWithFallback"

const InboxCard = ({
    name,
    phone,
    image,
    imageAlt = "Imagem da caixa de entrada",
    icon,
    status,
    actions = [],
    rightButton,
    platform,
    isSyncing,
    isCompleted,
    sync,
    getProgressPercent = () => 0,
    getElapsedTime = () => null,
    getSyncSpeed = () => null,
    getEstimatedTime = () => null
}) => {
    const [showActions, setShowActions] = useState(false)
    const menuRef = useRef(null)
    const hasTotals = Boolean(sync?.total && sync?.total > 0)
    const hasProcessed = Boolean(sync?.processed && sync?.processed > 0)
    const syncSpeed = isSyncing ? getSyncSpeed(sync) : null
    const estimatedTime = isSyncing ? getEstimatedTime(sync) : null
    const visibleActions = [
        ...(rightButton ? [{ ...rightButton, alwaysVisible: true }] : []),
        ...actions
    ].filter(action => status === "connected" || action.alwaysVisible)

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowActions(false)
            }
        }

        document.addEventListener("mousedown", handleClickOutside)
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [])

    const handleActionClick = (action) => {
        if (action.disabled) return

        setShowActions(false)
        action.onClick?.()
    }

    return (
        <div className={`${styles.inboxCard} ${showActions ? styles.menuOpen : ""}`}>
            <div className={`${styles.left} ${platform ? styles[platform] || "" : ""}`}>
                {image ?
                    <ImageWithFallback
                        src={image}
                        alt={imageAlt}
                        functionError={() => icon || <MdOutlineWhatsapp />}
                    />
                    :
                    icon || <MdOutlineWhatsapp />
                }
            </div>

            <div className={styles.center}>
                <div className={styles.topRow}>
                    <strong>{name || "-"}</strong>
                    {status && (
                        <span
                            className={`${styles.badge} ${status === "connected" ? styles.success : styles.danger}`}
                        >
                            {status === "connected" ? "Conectado" : "Desconectado"}
                        </span>
                    )}
                </div>

                {phone && <span className={styles.phone}>{phone}</span>}

                {isSyncing && (
                    <div className={styles.syncBox}>
                        <div className={styles.syncHeader}>
                            <span>
                                Sincronizando {sync?.type === "messages" ? "mensagens" : "contatos"}
                            </span>
                            <div className={styles.spinner} />
                        </div>

                        <div className={styles.progressBar}>
                            <div
                                className={styles.progressFill}
                                style={{ width: `${getProgressPercent(sync)}%` }}
                            />
                        </div>

                        {hasTotals && (
                            <small>
                                {sync?.processed || 0} de {sync?.total || 0}
                            </small>
                        )}

                        <small>
                            Tempo decorrido: {getElapsedTime(sync)}
                        </small>

                        {hasProcessed && syncSpeed && (
                            <>
                                <small>
                                    Velocidade: {syncSpeed} itens/s
                                </small>
                                {estimatedTime && (
                                    <small>
                                        Tempo restante: ~{estimatedTime}
                                    </small>
                                )}
                            </>
                        )}
                    </div>
                )}

                {isCompleted && (
                    <div className={styles.lastSync}>
                        ✅ Última sincronização concluída {sync?.finished_at && `em ${new Date(sync.finished_at).toLocaleString()}`}
                    </div>
                )}

            </div>

            {visibleActions.length > 0 && (
                <div className={styles.actionsMenu} ref={menuRef}>
                    {showActions && (
                        <button
                            type="button"
                            className={styles.menuBackdrop}
                            onClick={() => setShowActions(false)}
                            aria-label="Fechar menu de ações"
                        />
                    )}
                    <button
                        type="button"
                        className={styles.menuButton}
                        onClick={() => setShowActions(previousValue => !previousValue)}
                        aria-label="Ações da caixa de entrada"
                    >
                        <MdMoreVert />
                    </button>
                    {showActions && (
                        <div className={styles.menuList}>
                            {visibleActions.map((action, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    className={action.type === "danger" ? styles.dangerAction : action.type === "primary" ? styles.primaryAction : ""}
                                    onClick={() => handleActionClick(action)}
                                    disabled={action.disabled}
                                >
                                    {action.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default InboxCard
